import React, { useCallback, useRef, useState } from 'react';
import Head from 'next/head';
import { Box, Grid, Typography } from '@mui/material';

import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PersonIcon from '@mui/icons-material/Person';
import {
  PermissionsGate,
  TabsV2,
  TaskDetailScrollableSection,
  TaskUserDetails,
  TaskCarDetails,
  NewBookingModal,
  NewBookingForm,
  FAListCheckIcon,
  FAProductsIcon,
  FAFileContractIcon,
  FACalendar,
  BookingTicketDetails,
  BookingForm,
  BookingHistory,
  MoreButton,
  BookingSelectCustomerTypeModal,
  BookingLostModal,
  BookingPromisedDeliveryDateModal,
} from '@components';

import BookingSendSmsModal from 'components/bookings/BookingSendSms/BookingSendSmsModal/BookingSendSmsModal';
import BookingLostError from 'components/bookings/BookingLostError';
import SendMailFields from 'components/common/SendMailFields/SendMailFields';
import EditCarId from 'components/common/TaskCarDetails/EditCarId';

import { ModalV2 } from 'components/common/modals';
import { TaskSection } from 'components/common/TaskSection';
import { ButtonV2, FAHistoryIcon, FATableIcon } from 'components/common/ui';
import { UploadDocumentV3 } from 'components/common/media';

import { bookingsServices, communicationServices } from '@services';
import { showError, showSuccess, generateShortId } from '@utils';
import { DrawerLayout, TaskLayout } from '@layouts';
import {
  useBookingOperations,
  useToggleModal,
  useStateChangeRules,
} from 'hooks';
import { ENTITIES_PERMISIONS } from 'components/common/PermissionsGate/constants';
import { URLS } from 'layouts/DrawerLayout/constants';

import { getDefaultValues, getPayload } from 'helpers/bookings';
import {
  scrollableSectionHeightWithCardInfo,
  BOOKING_RULES_TYPES,
} from 'components/bookings/constants';

export const tabs = [
  {
    label: 'Booking Details',
    id: 'booking-details',
    icon: <FAFileContractIcon />,
  },
  { label: 'Products', id: 'products', icon: <FAProductsIcon /> },
  { label: 'Linked Items', id: 'linked-items', icon: <FATableIcon /> },
  { label: 'Payment & Billing', id: 'payment-billing', icon: <FACalendar /> },
  { label: 'History', id: 'history', icon: <FAHistoryIcon /> },
];

export const rightTabs = [
  {
    icon: <FAListCheckIcon />,
    id: 'ticket-details',
    label: 'Ticket Details',
  },
  {
    icon: <DirectionsCarIcon data-testid="DirectionsCarIcon" />,
    id: 'car-details',
    label: 'Vehicle Details',
  },
  {
    icon: <PersonIcon data-testid="PersonIcon" />,
    id: 'user-Information',
    label: 'User Information',
  },
  {
    icon: <InsertDriveFileIcon data-testid="InsertDriveFileIcon" />,
    id: 'documents',
    label: 'Files',
  },
];

const Booking = () => {
  const {
    booking,
    bookingId,
    isBookingLost,
    loading,
    user,
    vehicle,
    stages,
    assignees,
    loggedInUser,
    orders,
    agent,
    bookingHistoryQuery,
    customerTypesOptions,
    lostReasonOptions,
    bookingLabelsOptions,
    linkedTasks,
    getUser,
    getBooking,
    getChangeLog,
    getBookingLabels,
    updateBooking,
    handleCreateBookingLabel,
    handlePatchBookingLabel,
    invalidateBookingHistory,
  } = useBookingOperations(true);

  const {
    pendingStage,
    setPendingStage,
    isCustomerTypesModalOpen,
    handleCustomerTypesModalClose,
    isCarIdModalOpen,
    handleCarIdModalClose,
    isPromisedDeliveryDateModalOpen,
    handlePromisedDeliveryDateModalClose,
    handleStageChangeRules,
  } = useStateChangeRules();

  const { isOpen, handleClose, handleOpen } = useToggleModal();

  const {
    isOpen: isSmsOpen,
    handleClose: handleSmsClose,
    handleOpen: handleSmsOpen,
  } = useToggleModal();

  const {
    isOpen: isEmailOpen,
    handleClose: handleEmailClose,
    handleOpen: handleEmailOpen,
  } = useToggleModal();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const productsDirtyRef = useRef(false);
  const submitBtnRef = useRef();

  const handleProductsDirtyChange = useCallback((isDirty) => {
    productsDirtyRef.current = isDirty;
  }, []);

  const handleChangeVehicle = async (values) => {
    if (isBookingLost) return;

    try {
      setIsSubmitting(true);

      const payload = getPayload(values);
      await bookingsServices.addBooking({
        ...payload,
        previous_booking_id: bookingId,
      });
      await getBooking();
      await getChangeLog();
      setIsSubmitting(false);

      handleClose();
    } catch (error) {
      showError(error);
      setIsSubmitting(true);
    }
  };

  const handleChangeStage = async (id) => {
    if (isBookingLost) return;

    const stageNumber = Number(id);

    const canUpdateStage = handleStageChangeRules(
      booking,
      vehicle,
      stageNumber
    );

    if (!canUpdateStage) return;

    try {
      setIsSubmitting(true);
      await bookingsServices.updateBooking(bookingId, { stage: Number(id) });
      await getBooking();
      await getChangeLog();
      setPendingStage(null);
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeTicketDetails = async ({ field, value }) => {
    if (isBookingLost) return;

    if (field === 'stage') {
      const stageNumber = Number(value);

      const canUpdateStage = handleStageChangeRules(
        booking,
        vehicle,
        stageNumber
      );

      if (!canUpdateStage) return;
    }

    try {
      setIsSubmitting(true);
      let payload = {
        [field]: value,
      };
      if (field === 'assignee') {
        payload = {
          ticketDetails: {
            ...booking.ticketDetails,
            [field]: value,
          },
        };
      }
      if (field === 'labels' || field === 'orderId') {
        payload = {
          ticketDetails: {
            ...booking.ticketDetails,
            assignee: booking.ticketDetails?.assignee?.value,
            [field]: value,
          },
        };
      }

      if (field === 'wonTime') {
        await bookingsServices.updateBookingWonTime(bookingId, payload);
      } else {
        await bookingsServices.updateBooking(bookingId, payload);
      }

      await getBooking();
      await getChangeLog();
      setPendingStage(null);
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCarIdChange = async ({ field, value }) => {
    if (isBookingLost) return;

    try {
      setIsSubmitting(true);
      const payload = {
        vehicleDetails: {
          [field]: value,
        },
      };
      await bookingsServices.updateBooking(bookingId, payload);

      await getBooking();
      await getChangeLog();
      setDocuments([]);
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOnSuccessRuleModalClose = async (ruleType) => {
    const validRuleTypes = Object.values(BOOKING_RULES_TYPES);

    if (!ruleType || !validRuleTypes.includes(ruleType)) return;

    if (ruleType === BOOKING_RULES_TYPES.customerTypesStateChange) {
      handleCustomerTypesModalClose();
    } else if (ruleType === BOOKING_RULES_TYPES.carIdChange) {
      handleCarIdModalClose();
    } else if (ruleType === BOOKING_RULES_TYPES.promisedDeliveryDateChange) {
      handlePromisedDeliveryDateModalClose();
    }

    const {
      data: { data: newBooking },
    } = await getBooking();

    const canUpdateStage = handleStageChangeRules(
      newBooking,
      vehicle,
      pendingStage
    );
    if (canUpdateStage) {
      try {
        setIsSubmitting(true);
        await bookingsServices.updateBooking(bookingId, {
          stage: Number(pendingStage),
        });
        await getBooking();
        await getChangeLog();
        setPendingStage(null);
      } catch (error) {
        showError(error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleOnCancelRuleModalClose = (ruleType) => {
    if (ruleType === BOOKING_RULES_TYPES.customerTypesStateChange) {
      handleCustomerTypesModalClose();
    } else if (ruleType === BOOKING_RULES_TYPES.carIdChange) {
      handleCarIdModalClose();
    } else if (ruleType === BOOKING_RULES_TYPES.promisedDeliveryDateChange) {
      handlePromisedDeliveryDateModalClose();
    }
    setPendingStage(null);
  };

  const handleCarIdChangeAndMaybeStage = async ({ field, value }) => {
    try {
      setIsSubmitting(true);
      const payload = {
        vehicleDetails: {
          [field]: value,
        },
      };

      await bookingsServices.updateBooking(bookingId, payload);
      await getBooking();
      await getChangeLog();
      setDocuments([]);
      handleOnSuccessRuleModalClose(BOOKING_RULES_TYPES.carIdChange);
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (values) => {
    if (isBookingLost) return;

    const wereProductsDirty = productsDirtyRef.current;

    try {
      setIsSubmitting(true);
      await updateBooking({
        values,
        documents,
      });

      await getBooking();
      await getChangeLog();

      if (wereProductsDirty) {
        setShowOfferModal(true);
      }
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendOffer = async () => {
    if (isBookingLost) return;

    try {
      setIsSubmitting(true);
      await bookingsServices.sendOffer(bookingId);
      await getBooking();
      showSuccess('Offer sent successfully');
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
      setShowOfferModal(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (isBookingLost) return;

    try {
      setIsSubmitting(true);
      await bookingsServices.deleteDocument(bookingId, docId);
      await getBooking();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendEmail = async (payload) => {
    if (isBookingLost) return;

    try {
      setIsEmailSubmitting(true);

      const formData = new FormData();
      formData.append('from', payload.from);
      formData.append('body', payload.body);
      formData.append('subject', payload.subject);
      formData.append('mailboxId', payload.mailboxId);
      formData.append('entityId', booking.entityID);
      formData.append('entityType', 'booking');

      formData.append('cc', JSON.stringify(payload.cc));

      if (payload.bcc && payload.bcc.length > 0) {
        formData.append('bcc', JSON.stringify(payload.bcc));
      }

      if (payload.to && payload.to.length > 0) {
        formData.append('to', JSON.stringify(payload.to));
      }

      if (payload.attachments && payload.attachments.length > 0) {
        payload.attachments.forEach((file) => {
          formData.append('attachments', file);
        });
      }
      // send existing attachment ids from the template email (if any)
      if (payload.attachmentIds && payload.attachmentIds.length > 0) {
        formData.append('attachmentIds', JSON.stringify(payload.attachmentIds));
      }

      if (payload.templateId) {
        formData.append('templateId', payload.templateId);
      }
      if (payload.templateLang) {
        formData.append('templateLang', payload.templateLang);
      }

      await communicationServices.composeEmailV2(formData);

      showSuccess('Email sent successfully!');
      handleEmailClose();
    } catch (error) {
      showError(error);
    } finally {
      setIsEmailSubmitting(false);
    }
  };

  const handleUpdateGuarantor = async (values) => {
    if (isBookingLost) return;

    try {
      setIsSubmitting(true);
      await bookingsServices.updateGuarantorAgreement(
        bookingId,
        values?.id,
        values
      );
      getBooking();
      getChangeLog();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDeleteGuarantor = async (id) => {
    if (isBookingLost) return;

    try {
      setIsSubmitting(true);
      await bookingsServices.deleteGuarantorAgreement(bookingId, id);
      getBooking();
      getChangeLog();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // const handleSendOffer = async () => {
  //   try {
  //     setIsSubmitting(true);
  //     await bookingsServices.sendOffer(bookingId);
  //     showSuccess('The offer is send');
  //   } catch (error) {
  //     showError(error);
  //   } finally {
  //     setIsSubmitting(false);
  //   }
  // };

  const renderHeaderOptions = () => {
    return (
      <PermissionsGate entiry={ENTITIES_PERMISIONS.WriteBookings}>
        <Box sx={{ display: 'flex', gap: '8px' }}>
          {/* <ButtonV2
            variant="secondary"
            onClick={handleSendOffer}
            disabled={isSubmitting}
          >
            Send Offer
          </ButtonV2> */}
          <BookingLostModal
            loading={loading}
            isSubmitting={isSubmitting}
            bookingId={bookingId}
            isBookingLost={isBookingLost}
            lostReasonOptions={lostReasonOptions}
            onGetBooking={getBooking}
            onInvalidateBookingHistory={invalidateBookingHistory}
            onSetIsSubmitting={setIsSubmitting}
          />
          <ButtonV2
            ref={submitBtnRef}
            form="booking-details-form"
            type="submit"
            disabled={isSubmitting || isBookingLost}
            size="small"
          >
            Save
          </ButtonV2>
          <MoreButton
            size="small"
            disabled={isBookingLost}
            moreOptions={[
              {
                title: 'Template SMS',
                onClick: handleSmsOpen,
                entity: [ENTITIES_PERMISIONS.WriteBookings],
              },
              {
                title: 'Send Email',
                onClick: handleEmailOpen,
                entity: [ENTITIES_PERMISIONS.WriteBookings],
              },
              {
                title: 'Send Offer',
                onClick: handleSendOffer,
                entity: [ENTITIES_PERMISIONS.WriteBookings],
              },
            ]}
          />
        </Box>
      </PermissionsGate>
    );
  };
  const isRacBooking = booking?.ticketDetails?.labels?.includes('RAC');

  return (
    <>
      <Head>
        <title>{`Booking ID: ${bookingId}`}</title>
        <meta name="description" content="Instacar ledger" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <TaskLayout
        task={bookingId}
        entity={ENTITIES_PERMISIONS.ReadBookings}
        renderHeaderOptions={renderHeaderOptions}
        tabs={tabs}
        stages={stages}
        url={isRacBooking ? URLS.b2b : null}
        isLoading={loading}
        disabled={isBookingLost}
        currentStage={booking?.stage}
        onChangeStage={handleChangeStage}
      >
        {isBookingLost && (
          <BookingLostError
            booking={booking}
            stages={stages}
            lostReasonOptions={lostReasonOptions}
          />
        )}
        <Grid container alignItems="start" spacing={1}>
          <Grid size={{ xs: 12, md: 8 }}>
            <TabsV2 tabs={tabs} observerRootId="booking-details" />
            <TaskDetailScrollableSection
              id="booking-details"
              sectionStyles={
                isBookingLost
                  ? { ...scrollableSectionHeightWithCardInfo(90) }
                  : {}
              }
            >
              <BookingForm
                defaultValues={booking}
                user={user}
                loading={loading}
                disabled={isBookingLost}
                linkedTasks={linkedTasks}
                onSubmit={handleSubmit}
                onGetBooking={getBooking}
                onGetUser={getUser}
                onUpdateGuarantor={handleUpdateGuarantor}
                onDeleteGuarantor={handleDeleteGuarantor}
                onProductsDirtyChange={handleProductsDirtyChange}
              />
              <BookingHistory
                tabs={tabs}
                bookingHistoryQuery={bookingHistoryQuery}
              />
            </TaskDetailScrollableSection>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TabsV2
              tabs={rightTabs}
              observerRootId="booking-task-details-right"
              hideLabel
            />
            <TaskDetailScrollableSection
              id="booking-task-details-right"
              sectionStyles={
                isBookingLost
                  ? { ...scrollableSectionHeightWithCardInfo(110) }
                  : {}
              }
            >
              <TaskSection
                isSmall
                title={rightTabs[0].label}
                id={rightTabs[0].id}
                icon={rightTabs[0].icon}
                disabled={isBookingLost}
              >
                <BookingTicketDetails
                  isLoading={loading || isSubmitting}
                  task={booking}
                  orders={orders}
                  assignees={assignees}
                  stages={stages}
                  customerTypesOptions={customerTypesOptions}
                  bookingLabelsOptions={bookingLabelsOptions}
                  onChange={handleChangeTicketDetails}
                  getBookingLabels={getBookingLabels}
                  handleCreateBookingLabel={handleCreateBookingLabel}
                  handlePatchBookingLabel={handlePatchBookingLabel}
                />
              </TaskSection>

              <TaskSection
                isSmall
                title={rightTabs[1].label}
                id={rightTabs[1].id}
                icon={rightTabs[1].icon}
                disabled={isBookingLost}
              >
                <TaskCarDetails
                  vehicle={vehicle}
                  loading={loading}
                  handleOpen={handleOpen}
                  onChange={handleCarIdChange}
                  enableEdit
                />
              </TaskSection>
              <TaskSection
                isSmall
                title={rightTabs[2].label}
                id={rightTabs[2].id}
                icon={rightTabs[2].icon}
                disabled={isBookingLost}
              >
                {user.id ? (
                  <TaskUserDetails user={user} />
                ) : (
                  'No user billing info'
                )}
              </TaskSection>
              <TaskSection
                isSmall
                title={rightTabs[3].label}
                id={rightTabs[3].id}
                icon={rightTabs[3].icon}
                disabled={isBookingLost}
              >
                <UploadDocumentV3
                  key={`${generateShortId}`}
                  documents={booking?.documents}
                  onChange={setDocuments}
                  onDeleteDocument={handleDeleteDocument}
                  loading={loading || isSubmitting}
                />
              </TaskSection>
            </TaskDetailScrollableSection>
          </Grid>
        </Grid>
        <ModalV2
          title="Send Updated Offer?"
          isOpen={showOfferModal}
          onClose={() => setShowOfferModal(false)}
          isLoading={isSubmitting}
          maxWidth="sm"
          footer={
            <>
              <ButtonV2
                variant="outlined"
                onClick={() => setShowOfferModal(false)}
                loading={isSubmitting}
              >
                Not Now
              </ButtonV2>
              <ButtonV2 onClick={handleSendOffer} loading={isSubmitting}>
                Send Offer
              </ButtonV2>
            </>
          }
        >
          <Box sx={{ whiteSpace: 'pre-line' }}>
            <Typography variant="body1">
              You&apos;ve made changes to the booking products. A new,
              updated&nbsp;
              <b>offer.pdf</b> has been generated and saved to this booking
              Would you like to send the updated <b>offer.pdf</b> to the
              customer now?
            </Typography>
          </Box>
        </ModalV2>
        {isOpen && (
          <NewBookingModal
            isOpen={isOpen}
            title="Change Vehicle"
            defaultValues={getDefaultValues(booking, loggedInUser)}
            onClose={handleClose}
          >
            <NewBookingForm
              isSubmitting={isSubmitting}
              loading={loading}
              defaultValues={getDefaultValues(booking, loggedInUser)}
              onSubmit={handleChangeVehicle}
              hideAddProducts
            />
          </NewBookingModal>
        )}
        {isSmsOpen && (
          <BookingSendSmsModal
            isOpen={isSmsOpen}
            onClose={handleSmsClose}
            booking={booking}
          />
        )}
        {isEmailOpen && (
          <ModalV2
            isOpen={isEmailOpen}
            onClose={handleEmailClose}
            title="Send Email"
            maxWidth="md"
          >
            <SendMailFields
              showMailBoxSelector
              agent={agent}
              isSubmitting={isEmailSubmitting}
              onSendMail={handleSendEmail}
              initialReceivers={user.email ? [user.email] : []}
            />
          </ModalV2>
        )}
        <BookingSelectCustomerTypeModal
          isOpen={isCustomerTypesModalOpen}
          bookingId={bookingId}
          isSubmitting={isSubmitting}
          customerTypesOptions={customerTypesOptions}
          setIsSubmitting={setIsSubmitting}
          onSuccess={handleOnSuccessRuleModalClose}
          onClose={handleOnCancelRuleModalClose}
          onGetBooking={getBooking}
          onGetChangeLog={getChangeLog}
        />
        {isCarIdModalOpen && (
          <EditCarId
            hideTrigger
            modalId="booking-buy-car-car-id-modal"
            formId="booking-buy-car-car-id-form"
            open
            vehicleId={vehicle?.internalId}
            loading={loading || isSubmitting}
            onChange={handleCarIdChangeAndMaybeStage}
            onClose={() =>
              handleOnCancelRuleModalClose(BOOKING_RULES_TYPES.carIdChange)
            }
          />
        )}
        <BookingPromisedDeliveryDateModal
          isOpen={isPromisedDeliveryDateModalOpen}
          bookingId={bookingId}
          booking={booking}
          isSubmitting={isSubmitting}
          setIsSubmitting={setIsSubmitting}
          onSuccess={handleOnSuccessRuleModalClose}
          onClose={handleOnCancelRuleModalClose}
          onGetBooking={getBooking}
          onGetChangeLog={getChangeLog}
        />
      </TaskLayout>
    </>
  );
};

Booking.getLayout = (page) => {
  return <DrawerLayout>{page}</DrawerLayout>;
};

export default Booking;
